import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { HelpCircle } from "lucide-react";

const fmt = (v, d = 1) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d).replace(".", ","));

export const MetaCalculationInfo = ({ goal, context, drc, wetGrams, dryGrams }) => {
  if (!goal?.target_grams) return null;
  const base = goal.base_grams;
  const coef = goal.seasonal_coefficient;
  const bonus = goal.stimulation_bonus ?? 1;
  const target = goal.target_grams;
  const clone = context?.clone || "clone";
  const usesStim = context?.uses_stimulation;
  const stimPct = context?.stimulation_pct;
  const drcVal = drc || 53;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-gray-400 hover:text-emerald-600 transition-colors align-middle"
          aria-label="Como a meta é calculada?"
          data-testid="meta-calc-info-btn"
          onClick={(e) => e.stopPropagation()}
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-sm" data-testid="meta-calc-popover" onClick={(e) => e.stopPropagation()}>
        <p className="font-semibold text-gray-800 mb-2">📐 Como a meta é calculada?</p>
        <p className="text-xs text-gray-500 mb-3">Meta = Base do Clone × Coef. Sazonal × Estimulação</p>

        <div className="space-y-2 text-xs">
          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-600">🌳 Meta base ({clone})</span>
            <span className="font-medium whitespace-nowrap">{fmt(base)}g</span>
          </div>
          <p className="text-[11px] text-gray-400 -mt-1">
            Potencial genético do clone, em borracha <strong>seca</strong> por árvore/corte (IAC, EMBRAPA, APABOR).
          </p>

          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-600">🗓️ Coef. sazonal{goal.seasonal_description ? ` (${goal.seasonal_description})` : ""}</span>
            <span className="font-medium whitespace-nowrap">×{fmt(coef, 2)}</span>
          </div>
          <p className="text-[11px] text-gray-400 -mt-1">
            Ajusta a meta ao mês: pico de inverno cobra mais, senescência cobra menos. Justiça sazonal.
          </p>

          <div className="flex justify-between items-start gap-2">
            <span className="text-gray-600">💉 Estimulação {usesStim ? `(${stimPct}%)` : "(não usa)"}</span>
            <span className="font-medium whitespace-nowrap">×{fmt(bonus, 2)}</span>
          </div>
          <p className="text-[11px] text-gray-400 -mt-1">
            {usesStim
              ? `Ethephon a ${stimPct}% aumenta a produção por corte, então a meta sobe ${fmt((bonus - 1) * 100, 0)}%.`
              : "Sem estimulação, a meta não recebe bônus."}
          </p>
        </div>

        <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800" data-testid="meta-calc-result">
          🎯 <strong>{fmt(base)}g × {fmt(coef, 2)} × {fmt(bonus, 2)} = {fmt(target)}g</strong>/árvore/corte (borracha seca)
        </div>

        {wetGrams > 0 && dryGrams > 0 && wetGrams !== dryGrams && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800" data-testid="meta-calc-drc">
            🧪 Sua produção: <strong>{fmt(wetGrams)}g</strong> de coágulo úmido × DRC {fmt(drcVal, 0)}% = <strong>{fmt(dryGrams)}g</strong> de borracha seca — é esse valor que é comparado com a meta de {fmt(target)}g.
          </div>
        )}

        <p className="text-[11px] text-gray-400 mt-2">
          ℹ️ O sistema de sangria ({context?.system || "D/4"}) define o <strong>ritmo</strong> de cortes no ano — não altera a meta por corte, garantindo comparação justa entre sistemas.
        </p>
      </PopoverContent>
    </Popover>
  );
};
